/**
 * A router for four static pages.
 *
 * The application is one view plus the legal documents, which is well under
 * the weight of a routing library. The backend already serves index.html for
 * any non-/api path, so real URLs work on refresh and can be linked to.
 */

import { useCallback, useEffect, useState, type ReactNode } from "react";

/** navigate(), factored out so setSearchParam can reuse it. */
function navigateTo(url: string): void {
  if (window.location.pathname + window.location.search === url) return;
  window.history.pushState({}, "", url);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/**
 * Targets are compared as path *and* query string: "/?county=12086" and "/"
 * are different destinations, and the coverage map's links differ only in the
 * query.
 */
export function navigate(target: string): void {
  navigateTo(target);
}

export function usePathname(): string {
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const update = () => setPath(window.location.pathname);
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);

  return path;
}

/**
 * One query-string parameter, kept in sync with Back and Forward.
 *
 * The selected county lives in the URL rather than in component state so that
 * a county view can be linked, bookmarked and reloaded — the coverage map
 * navigates by writing this parameter, and the dashboard reads it back.
 */
export function useSearchParam(name: string): string {
  const read = useCallback(
    () => new URLSearchParams(window.location.search).get(name) ?? "",
    [name],
  );
  const [value, setValue] = useState(read);

  useEffect(() => {
    const update = () => setValue(read());
    update();
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, [read]);

  return value;
}

/**
 * Sends the browser somewhere else on mount, without leaving a history entry.
 *
 * Used for URLs that stop making sense once state changes — /signin after you
 * have signed in. `replaceState` rather than `pushState`, so Back goes to
 * wherever you came from and not to a page that would bounce you again.
 */
export function Redirect({ to }: { to: string }) {
  useEffect(() => {
    if (window.location.pathname + window.location.search === to) return;
    window.history.replaceState({}, "", to);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, [to]);

  return null;
}

/** Replaces the query string on the current path, adding a history entry. */
export function setSearchParam(name: string, value: string): void {
  const params = new URLSearchParams(window.location.search);
  if (value) params.set(name, value);
  else params.delete(name);
  const search = params.toString();
  navigateTo(window.location.pathname + (search ? `?${search}` : ""));
}

/**
 * Replaces the whole query string in place, leaving no history entry.
 *
 * `replaceState`, unlike `setSearchParam`'s push, because the caller is the
 * report basket: it mirrors every control into the URL so a price can be
 * reloaded and sent to somebody else, and a pushed entry per slider movement
 * would make Back walk backwards through a hundred intermediate baskets
 * instead of leaving the page.
 */
export function replaceSearch(search: string): void {
  const next = window.location.pathname + search;
  if (window.location.pathname + window.location.search === next) return;
  window.history.replaceState({}, "", next);
}

interface LinkProps {
  to: string;
  children: ReactNode;
  className?: string;
  /** Legal links from the signup form open in a tab so the form isn't lost. */
  newTab?: boolean;
}

export function Link({ to, children, className, newTab }: LinkProps) {
  const onClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      // Let the browser handle modified clicks — middle-click, cmd-click and
      // ctrl-click should still open a tab.
      if (newTab || event.metaKey || event.ctrlKey || event.shiftKey ||
          event.button !== 0) {
        return;
      }
      event.preventDefault();
      navigate(to);
      window.scrollTo(0, 0);
    },
    [to, newTab],
  );

  return (
    <a
      href={to}
      className={className}
      onClick={onClick}
      {...(newTab ? { target: "_blank", rel: "noreferrer" } : {})}
    >
      {children}
    </a>
  );
}
